// @react
import React, { useState, ReactElement, useRef, useEffect } from 'react'
// @libraries
import DocumentTitle from 'react-document-title'
import { makeStyles, Theme } from '@material-ui/core/styles'
// @context
import { PersonalInformationContext } from 'modules/profile/context'
// @components
import PageTitle from '@bit/atd.web.page-title'
// @module components
import PageWrapper from 'modules/profile/components/PageWrapper'
import Visibility from 'modules/profile/components/Visibility'
import Bio from 'modules/profile/components/Bio'
import Job from 'modules/profile/components/Job'
import Education from 'modules/profile/components/Education'
import ContactInformation from 'modules/profile/components/ContactInformation'

/**
 * 5.0.1 Profile - Personal Information
 * https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=866%3A3045
 */

const useStyles = makeStyles((theme: Theme) => ({
  innerPadding: {
    '& > div': {
      marginBottom: '24px',
      '&:last-child': {
        marginBottom: '0',
      },
    },
  },
}))

type SectionStateType = null | 'bio' | 'job' | 'education' | 'information'
type PropsType = any

const PersonalInformation = (props: PropsType): ReactElement => {
  /******************************************
   * STATE
   ******************************************/

  const [editingSection, setEditingSection] = useState<SectionStateType>(null)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()
  const bioRef = useRef<HTMLDivElement | null>(null)
  const jobRef = useRef<HTMLDivElement | null>(null)
  const eduRef = useRef<HTMLDivElement | null>(null)
  const infRef = useRef<HTMLDivElement | null>(null)
  const prevSectionType = useRef<string | null>()

  // once editing is done
  // scroll to top of last edited section
  // else scroll to top of page
  useEffect(() => {
    if (prevSectionType.current !== editingSection) {
      switch (prevSectionType.current) {
        case 'bio':
          scrollToSection(bioRef.current)
          break
        case 'job':
          scrollToSection(jobRef.current)
          break
        case 'education':
          scrollToSection(eduRef.current)
          break
        case 'information':
          scrollToSection(infRef.current)
          break
        default:
          scrollToSection(null)
          break
      }
      prevSectionType.current = editingSection
    }
  }, [editingSection])

  /******************************************
   * RENDER
   ******************************************/

  return (
    <PageWrapper>
      <DocumentTitle title={`Profile - Personal Information | ATD`}>
        <PersonalInformationContext.Provider
          value={{ editingSection, setEditingSection }}
        >
          <PageTitle>Personal Information</PageTitle>
          <div className={classes.innerPadding}>
            {!editingSection && <Visibility />}
            {(!editingSection || editingSection === 'bio') && (
              <Bio ref={bioRef} />
            )}
            {(!editingSection || editingSection === 'job') && (
              <Job ref={jobRef} />
            )}
            {(!editingSection || editingSection === 'education') && (
              <Education ref={eduRef} />
            )}
            {(!editingSection || editingSection === 'information') && (
              <ContactInformation ref={infRef} />
            )}
          </div>
        </PersonalInformationContext.Provider>
      </DocumentTitle>
    </PageWrapper>
  )
}

export default PersonalInformation

function scrollToSection(ref: HTMLDivElement | null) {
  if (!ref) {
    try {
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    } catch (error) {
      window.scrollTo(0, 0)
    }
  } else {
    try {
      window.scroll({
        top: ref.offsetTop,
        left: 0,
        behavior: 'smooth',
      })
    } catch (error) {
      window.scrollTo(0, ref.offsetTop)
    }
  }
}
